import { EXPLICIT_UNKNOWN_SOURCE_DISPLAY, isUndefined, schSourceType } from '@aloft/toolkit-shared';
import { Flex, Icon, Tooltip, Typography, theme } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import { Button, Popover, PopoverContent, PopoverTrigger } from '@aloft/toolkit-sui/chakra';

import { useRef } from 'react';
import { Controller, ControllerRenderProps, FieldError, FieldPath } from 'react-hook-form';

import { useAppDispatch } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { TruncatedText } from './shared/TruncatedText.tsx';
import { usePropertyField } from './shared/usePropertyField.tsx';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { emptyFieldStyle, sharedButtonStyle } from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';

const IconElement = ({ isOpen, error }: { isOpen: boolean; error?: FieldError }) =>
  error ? (
    <Tooltip label={error.message}>
      <Icon icon="error-warning" color="alertError" />
    </Tooltip>
  ) : (
    <Icon icon={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'} />
  );

export const EditableEnumField = ({
  columnIndex,
  field,
  isArv = false,
  colSpan = 1,
  control,
  getValues,
  setValue,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const { options, includeEmptyOption, toValue } = field;

  const { propertyId, propertyDataFieldName, selectedSourceFieldName, displayValue } =
    usePropertyField({
      columnIndex,
      field,
      isArv,
      control,
      getValues,
    });

  const fieldRef = useRef<HTMLButtonElement>(null);
  const { isAlertTarget, clearAlertTarget } = usePropertyGridCellFocus({
    cellKeyId: field.fieldName,
    propertyId,
    fieldRef,
  });

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string | number,
  ) => {
    if (isUndefined(newValue)) return;

    const parsedNewValue = toValue
      ? (toValue(newValue.toString(), propertyId) as string | number)
      : newValue;
    onChange(parsedNewValue);

    setValue(
      `${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>,
      schSourceType.enum.custom,
    );
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
    clearAlertTarget();
  };

  if (!options) return null;

  return (
    <Controller
      name={propertyDataFieldName}
      control={control}
      render={({ field: { onChange, ref }, fieldState: { error } }) => {
        const matchingOption = options.find((opt) => opt.value === displayValue);
        const triggerLabel =
          matchingOption?.label ??
          (displayValue === EXPLICIT_UNKNOWN_SOURCE_DISPLAY ? displayValue : undefined);

        return (
          <Popover gutter={-32} variant="inline" isLazy matchWidth>
            {({ isOpen, onClose }) => {
              const handleOnClose = () => {
                if (document.activeElement instanceof HTMLElement) document.activeElement?.blur();
                onClose();
              };

              const { title, backgroundColor } = emptyFieldStyle(isUndefined(displayValue));
              const alertBg = isAlertTarget ? theme.colors.alertErrorLight : backgroundColor;
              const alertBorder = isAlertTarget
                ? `2px solid ${theme.colors.alertError}`
                : undefined;

              return (
                <>
                  <PopoverTrigger>
                    <Button
                      {...sharedButtonStyle}
                      ref={(el) => {
                        ref(el);
                        (fieldRef as React.MutableRefObject<HTMLButtonElement | null>).current = el;
                      }}
                      title={isAlertTarget ? 'This field is causing an active alert.' : title}
                      backgroundColor={alertBg}
                      gridColumn={`span ${colSpan}`}
                      onClick={handleOnClose}
                      __dangerDoNotUseSx={alertBorder ? { border: alertBorder } : undefined}
                    >
                      <Flex alignItems="center" justifyContent="space-between" width="100%">
                        <TruncatedText>{triggerLabel}</TruncatedText>
                        <IconElement isOpen={isOpen} error={error} />
                      </Flex>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <Button
                      {...sharedButtonStyle}
                      alignItems="center"
                      justifyContent="space-between"
                      gap="4px"
                      borderWidth="0px"
                      borderStyle="solid"
                      borderColor="strokeDefault"
                      borderBottomWidth="1px"
                      backgroundColor="transparent"
                      onClick={handleOnClose}
                    >
                      <Typography variant="body1">{triggerLabel}</Typography>
                      <IconElement isOpen={isOpen} error={error} />
                    </Button>
                    <Flex flexDirection="column" gap="1px" maxHeight="25vh" overflowY="auto">
                      {includeEmptyOption && (
                        <Button
                          title="None"
                          backgroundColor={theme.colors.surface1}
                          {...sharedButtonStyle}
                          onClick={() => {
                            handleSetFieldValue(onChange, '');
                            handleOnClose();
                          }}
                        />
                      )}
                      {options.map((option) => (
                        <Button
                          key={option.value}
                          title={option.value.toString()}
                          backgroundColor={theme.colors.surface1}
                          {...sharedButtonStyle}
                          onClick={() => {
                            handleSetFieldValue(onChange, option.value);
                            handleOnClose();
                          }}
                        >
                          <Flex alignItems="center" justifyContent="space-between" width="100%">
                            <TruncatedText>
                              <Typography variant="body1">{option.label}</Typography>
                            </TruncatedText>
                          </Flex>
                        </Button>
                      ))}
                    </Flex>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};
